import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ECheqRequestFilter } from "@/components/common/section-components/filters/ECheqRequestFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { TransferRequestActions } from "@/components/common/section-components/action/TransferRequestActions";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { CheckRequestStatus } from "@/constants/entity-status";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";
import { FieldValues, useForm } from "react-hook-form";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { formatCurrency } from "@/utils/format-currency";
import { ECheqRequestHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  fetchECheqRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  ECheqRequestItem,
  ECheqRequestListResponse,
} from "./schema/echeq-requests.schema";

export const ECheqRequestsPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { handleSubmit, setValue, getValues, control } = useForm();
  const { fetchPermissions } = useRoles();
  const navigate = useNavigate();

  const [loadingECheqRequests, setLoadingECheqRequests] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});
  const [selection, setSelection] = useState<string[]>([]);

  const [echeqRequests, setECheqRequests] = useState<ECheqRequestListResponse>({
    listado: [],
    pendingRequests: false,
    totalPages: 1,
    totalRecords: 10,
  });

  const fetchECheqRequests = async (filters?: any, showLoad = true) => {
    await fetchECheqRequestsHandler({
      filters,
      showLoad,
      setLoadingECheqRequests,
      setECheqRequests,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchECheqRequests();
    }
  }, [isActive]);

  usePolling({
    callback: () => {
      if (!loadingECheqRequests) {
        fetchECheqRequests({ ...currentFilters, "P.Pagina": page }, false);
      }
    },
    delay: 10000,
    isActive: isActive && !loadingECheqRequests,
  });

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchECheqRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      fetchPermissions,
      navigate,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchECheqRequests({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.ECHEQ_REQUEST_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: ECheqRequestItem) => (
    <TransferRequestActions handleDetail={handleDetail} item={item as any} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Solicitudes de E-Cheq" loading={loadingECheqRequests} />

      <ECheqRequestFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        submitFilters={submitFilters}
        control={control}
        disableFilters={loadingECheqRequests}
      />

      <Flex flexDir="column">
        {!loadingECheqRequests && !echeqRequests?.listado?.length && <NoData />}
        {!loadingECheqRequests && !!echeqRequests?.listado?.length && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={false}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                echeqRequests?.listado?.map((el) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: `$${formatCurrency(el?.monto)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as CheckRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={ECheqRequestHeaderElements}
              actionElements={getActionElements}
            />
            {echeqRequests?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={echeqRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
